// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Primordia — Meeting Intelligence
//
// Most discovery knowledge is SPOKEN. It is said once, in an interview with an
// application owner, and then it is gone: the consultant who heard it types a
// tenth of it into a fact sheet that evening, and the rest evaporates. Six
// months later nobody can answer "where did we learn that?".
//
// Every meeting platform already produces a transcript. So this is not a
// "record audio" problem, it is a "read the transcript" problem — which is the
// same shape as DocumentExtract.js, and it reuses the same endpoint and the
// same safety posture:
//
//   * /ai/extract is only ever READ from. It returns PROPOSALS. Nothing is
//     written to a fact sheet until a human presses Accept on that one row.
//   * The backend already drops any proposal whose evidence quote does not
//     literally occur in the text that was submitted. This screen does NOT
//     re-implement or weaken that — it locates the quote in the transcript a
//     second time so it can show the reviewer the sentence, the speaker and
//     the timestamp, and so a quote that cannot be located is visibly marked.
//   * A proposal with no quote at all is UNVERIFIED, is never pre-selected,
//     and sorts to the bottom of the list.
//   * Accepting a fact writes the value AND a citation — meeting title, date,
//     speaker, quote. A fact with no traceable source is barely better than a
//     guess, so the citation is written in the same call as the value.
//
// TWO THINGS THAT MATTER MORE THAN THE FEATURE
//
// 1. A REAL MEETING RECORDING IS NOT A DISCOVERY DOCUMENT. The same hour that
//    covers interface engines also covers what the client is paying, who is
//    being replaced, and what somebody thinks of their director. So the
//    transcript itself NEVER leaves this browser: it lives in localStorage
//    under 'primordia.transcripts.<tenantId>', capped, and it is never handed
//    to store.saveFactSheet. Only accepted facts and their short quotes are
//    saved to the workspace. Slices of the transcript are sent to /ai/extract
//    to be read — that is the one place text leaves the browser, and the
//    screen says so.
//
// 2. THE QUOTE IS A FRAGMENT OF A REAL CONVERSATION. What gets stored is the
//    sentence carrying the evidence, not the surrounding paragraph, and the
//    screen states plainly that the quote is saved with the fact and is
//    visible to anyone who can see that workspace.
//
// Pulling transcripts straight out of Grain or Teams is rendered as DISABLED
// controls with the reason on screen: it needs an API credential that is not
// configured. Nothing here fetches anything; every transcript on this screen
// was pasted or uploaded by hand, and the Sources tab records which.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
  LinearProgress, MenuItem, Paper, Stack, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { allFieldsFor, FIELD_TYPES } from './schema';
// Reused deliberately: the same "is this value even legal for this field?" gate
// AiEnrichment and DocumentExtract use, so all three screens reject the same
// things rather than each inventing its own idea of a valid value.
import { coerceSuggestion } from './AiEnrichment';
import { getConfig } from './config';
import { getIdToken } from './auth';
import store from './store';

// ---- Optimum palette (re-theme incoming; these mirror the sibling screens) --
const NAVY = '#14284A';
const BLUE = '#1B6FB3';
const GREY = '#5C6B7A';
const AMBER = '#B26A00';
const GREEN = '#2E7D32';
const RED = '#B0342C';
const HEAD = 'sectionHeader.main';
const headSx = { '& th': { fontWeight: 700, color: HEAD } };

// ---------------------------------------------------------------------------
// Local storage budget.
//
// A two-hour transcript is comfortably 150k characters and localStorage is a
// few megabytes for the WHOLE origin — which this app also uses for the fact
// sheet cache. Blowing that quota would take the CMDB down with it, so the cap
// is enforced on the way in (per transcript), on the number of transcripts
// kept, and again on write, where the oldest transcript TEXT is evicted before
// anything else is lost. The metadata and the accepted-fact log are tiny and
// are what the Sources tab needs, so they are the last thing to go.
// ---------------------------------------------------------------------------
const LS_PREFIX = 'primordia.transcripts.';
const lsKey = (tid) => `${LS_PREFIX}${tid || 'unknown'}`;
const MAX_STORED_CHARS = 120000;   // per transcript, kept in this browser
const MAX_TRANSCRIPTS = 20;        // per workspace
// What we send per application in one /ai/extract call. The backend truncates
// at 60k of its own accord; staying under it means we are never silently
// reading half a slice.
const MAX_SLICE_CHARS = 45000;
// Segments of context either side of a line that names the application.
const CONTEXT_SEGMENTS = 4;
// The stored quote is the evidence sentence, not the paragraph around it.
const QUOTE_MAX = 240;
const GAP_MARKER = '\n\n… (unrelated part of the meeting omitted) …\n\n';

const TEXT_EXTS = ['txt', 'md', 'markdown', 'vtt', 'srt', 'text'];
const ACCEPT_ATTR = '.txt,.md,.vtt,.srt';

const isBlank = (v) => v === undefined || v === null || v === '';
const clean = (s) => String(s == null ? '' : s).trim();
const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const displayValue = (v) => {
  if (isBlank(v)) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

const truncate = (s, n) => {
  const str = String(s || '');
  return str.length > n ? `${str.slice(0, n)}…` : str;
};

function fmtClock(sec) {
  if (sec == null || Number.isNaN(sec)) return '';
  const total = Math.floor(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

// ---------------------------------------------------------------------------
// TRANSCRIPT PARSING
//
// Three shapes turn up in practice and all three are handled:
//
//   WebVTT  — "WEBVTT" header, then cues of
//             "00:01:23.000 --> 00:01:27.500" followed by the spoken text.
//             Teams marks the speaker inside the cue as "<v Frank Scalzo>…</v>".
//   SRT     — a cue index line, then "00:01:23,000 --> 00:01:27,500", then text.
//             Same as VTT bar the comma and the index.
//   Plain   — what Grain, Teams and Zoom exports usually look like once pasted:
//             "Speaker Name: text", sometimes with a timestamp in front
//             ("[00:12:33] Speaker: …", "00:12:33 Speaker: …") and sometimes as
//             a header line of its own ("Frank Scalzo   00:12:33" followed by
//             the spoken lines).
//
// Everything is parsed into the SAME segment shape, each carrying its exact
// character offsets in the transcript. Those offsets are the whole point: they
// are how a quote returned by the model is later attributed to a speaker and a
// timestamp, instead of the model being asked (and trusted) to attribute it.
//
// Anything that cannot be recognised is still kept as a segment with no
// speaker and no time. An unparsed line is not a lost line.
// ---------------------------------------------------------------------------

const CUE_LINE = /^\s*((?:\d{1,3}:)?\d{1,2}:\d{2}(?:[.,]\d{1,3})?)\s*-->\s*((?:\d{1,3}:)?\d{1,2}:\d{2}(?:[.,]\d{1,3})?)/;
const CLOCK_ONLY = /^(?:(\d{1,3}):)?(\d{1,2}):(\d{2})(?:[.,](\d{1,3}))?$/;
const LEAD_CLOCK = /^\s*[[(]?((?:\d{1,3}:)?\d{1,2}:\d{2}(?:[.,]\d{1,3})?)[\])]?\s*[-–—]?\s*/;
const TRAIL_CLOCK = /^(.{1,60}?)[\s ]+[[(]?((?:\d{1,3}:)?\d{1,2}:\d{2}(?:[.,]\d{1,3})?)[\])]?\s*$/;
const SPEAKER_PREFIX = /^([^:]{1,60}):\s*(.*)$/;
const VTT_VOICE = /^<v[^>]*?[\s.]([^>]+)>([\s\S]*?)(?:<\/v>)?\s*$/;

function clockToSeconds(str) {
  const m = CLOCK_ONLY.exec(clean(str));
  if (!m) return null;
  const h = m[1] ? Number(m[1]) : 0;
  const frac = m[4] ? Number(`0.${m[4]}`) : 0;
  return (h * 3600) + (Number(m[2]) * 60) + Number(m[3]) + frac;
}

// Words that appear before a colon in meeting notes but are not people. Without
// this, "Action items:", "Attendees:" and "Note:" each become a "speaker" and
// the speaker count stops meaning anything.
const NOT_SPEAKERS = new Set([
  'action', 'action item', 'action items', 'agenda', 'attendees', 'chapters',
  'date', 'decision', 'decisions', 'duration', 'follow up', 'follow-up',
  'highlights', 'key points', 'meeting', 'next steps', 'note', 'notes',
  'participants', 'recording', 'summary', 'time', 'title', 'todo', 'to do',
  'topic', 'topics', 'transcript', 'http', 'https',
]);

// Is this plausibly a person's label rather than the first clause of a
// sentence? Deliberately strict: a false speaker is worse than a missing one,
// because it is then attached to a quote saved into a client's workspace.
function isSpeakerLabel(name) {
  const n = clean(name);
  if (!n || n.length > 60) return false;
  if (!/[A-Za-z]/.test(n)) return false;
  if (NOT_SPEAKERS.has(n.toLowerCase())) return false;
  const words = n.split(/\s+/);
  if (words.length > 6) return false;
  // "Dana Whitfield  00" — a stray fragment of a timestamp, not a person.
  if (/^\d+$/.test(words[words.length - 1])) return false;
  // Sentence punctuation inside the label means it is prose, not a name. A
  // short token ending in "." is allowed so "Dr." and initials survive.
  if (words.some((w) => /[.!?]$/.test(w) && w.length > 4)) return false;
  if (/[.!?][^\s]/.test(n)) return false;
  return true;
}

// Pull a speaker off the front of a line of plain-text transcript.
// `meta` marks the known non-speaker prefixes ("Action items:", "Attendees:"):
// those END the previous speaker's block rather than continuing it, so a note
// typed under someone's last sentence is never quoted as their words.
function splitSpeaker(line) {
  const m = SPEAKER_PREFIX.exec(line);
  if (!m) return { speaker: '', text: line };
  const name = clean(m[1]);
  // "Frank Scalzo (00:12:33): …" — the clock rides along with the name.
  const withClock = TRAIL_CLOCK.exec(name);
  const bare = withClock ? clean(withClock[1]) : name;
  if (NOT_SPEAKERS.has(bare.toLowerCase())) return { speaker: '', text: line, meta: true };
  if (!isSpeakerLabel(bare)) return { speaker: '', text: line };
  return {
    speaker: bare,
    text: m[2],
    time: withClock ? clockToSeconds(withClock[2]) : null,
  };
}

// Split into lines while keeping exact offsets into the (already newline-
// normalised) transcript, so a quote can be traced back to the line that
// carried it.
function lineIndex(text) {
  const out = [];
  let pos = 0;
  text.split('\n').forEach((raw) => {
    out.push({ raw, offset: pos, end: pos + raw.length });
    pos += raw.length + 1;
  });
  return out;
}

function detectFormat(text) {
  const head = text.slice(0, 400);
  if (/^﻿?WEBVTT/.test(head)) return 'vtt';
  if (CUE_LINE.test(head) || /\n\s*\d+\s*\n\s*\d{2}:\d{2}:\d{2},\d{3}\s*-->/.test(text.slice(0, 2000))) {
    return /,\d{3}\s*-->/.test(text) ? 'srt' : 'vtt';
  }
  return 'lines';
}

// WebVTT / SRT: cue timings on one line, spoken text on the following lines.
function parseCues(text) {
  const lines = lineIndex(text);
  const segments = [];
  let cueStart = null;
  let cueEnd = null;
  let pendingSpeaker = '';

  lines.forEach((ln) => {
    const raw = ln.raw;
    const trimmed = clean(raw);
    const cue = CUE_LINE.exec(raw);
    if (cue) {
      cueStart = clockToSeconds(cue[1]);
      cueEnd = clockToSeconds(cue[2]);
      pendingSpeaker = '';
      return;
    }
    if (!trimmed) return;
    if (/^﻿?WEBVTT/.test(trimmed) || /^NOTE\b/.test(trimmed)) return;
    // An SRT cue index. A caption line is never bare digits, so dropping these
    // costs nothing; leaving them in put "2" in the transcript as if it were
    // something a person said.
    if (/^\d+$/.test(trimmed)) return;

    let speaker = pendingSpeaker;
    let body = trimmed;
    const voice = VTT_VOICE.exec(trimmed);
    if (voice) {
      speaker = clean(voice[1]);
      body = clean(voice[2]);
    } else {
      const split = splitSpeaker(trimmed);
      if (split.meta) speaker = '';
      else if (split.speaker) { speaker = split.speaker; body = clean(split.text); }
    }
    pendingSpeaker = speaker;
    segments.push({
      speaker,
      time: cueStart,
      endTime: cueEnd,
      raw,
      text: body.replace(/<[^>]+>/g, ''),
      offset: ln.offset,
      end: ln.end,
    });
  });

  return segments;
}

// "Speaker Name: text" exports, with or without timestamps, plus the
// header-line variant Grain and Zoom produce.
function parseLines(text) {
  const lines = lineIndex(text);
  const segments = [];
  let speaker = '';
  let time = null;

  lines.forEach((ln) => {
    const trimmed = clean(ln.raw);
    if (!trimmed) return;

    let body = trimmed;
    let lineTime = null;
    const lead = LEAD_CLOCK.exec(body);
    if (lead) {
      lineTime = clockToSeconds(lead[1]);
      body = body.slice(lead[0].length);
    }

    // A speaker header on a line of its own, which is how Grain and Zoom
    // export: "Frank Scalzo   00:12:33", with the spoken lines beneath it. The
    // name itself must not contain a colon, or "Dana: it is 00:12" would be
    // mistaken for a header.
    const header = TRAIL_CLOCK.exec(body);
    if (header && !header[1].includes(':') && isSpeakerLabel(header[1])) {
      speaker = clean(header[1]);
      time = clockToSeconds(header[2]);
      return;
    }

    const split = splitSpeaker(body);
    if (split.meta) {
      speaker = '';       // "Action items:" ends the last speaker's block
    } else if (split.speaker) {
      speaker = split.speaker;
      if (split.time != null) lineTime = split.time;
      body = clean(split.text);
    }
    if (lineTime != null) time = lineTime;
    if (!body) return;

    segments.push({
      speaker,
      time,
      endTime: null,
      raw: ln.raw,
      text: body,
      offset: ln.offset,
      end: ln.end,
    });
  });

  return segments;
}

function parseTranscript(rawText) {
  // Normalise line endings ONCE, up front. Every offset in every segment — and
  // therefore every speaker attribution — is an offset into this string, so it
  // is also the string that gets stored and sent.
  const text = String(rawText || '').replace(/\r\n?/g, '\n');
  if (!clean(text)) {
    return {
      text: '', format: 'empty', segments: [], speakers: [], durationSec: null,
      lineCount: 0, timedCount: 0, charCount: 0,
    };
  }
  const format = detectFormat(text);
  const segments = format === 'lines' ? parseLines(text) : parseCues(text);
  const speakers = [];
  let durationSec = null;
  let timedCount = 0;
  segments.forEach((s) => {
    if (s.speaker && !speakers.includes(s.speaker)) speakers.push(s.speaker);
    if (s.time != null) {
      timedCount += 1;
      const stamp = s.endTime != null ? s.endTime : s.time;
      if (durationSec == null || stamp > durationSec) durationSec = stamp;
    }
  });
  return {
    text,
    format,
    segments,
    speakers,
    durationSec,
    lineCount: text.split('\n').filter((l) => clean(l)).length,
    timedCount,
    charCount: text.length,
  };
}

const FORMAT_LABEL = {
  vtt: 'WebVTT captions',
  srt: 'SRT captions',
  lines: 'speaker-labelled lines',
  empty: 'nothing yet',
};

// ---------------------------------------------------------------------------
// Locating a quote in the transcript.
//
// This duplicates the small whitespace-insensitive index in DocumentExtract.js
// rather than importing it, because that module does not export it and this
// screen is a single self-contained file. It is not a second gate on the
// backend's literal-quote check — it exists so the reviewer can be shown the
// SPEAKER and TIMESTAMP behind the quote, which is only knowable from the
// position of the quote in the transcript.
// ---------------------------------------------------------------------------
function buildSearchIndex(text) {
  let norm = '';
  const map = [];
  let lastWasSpace = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text.charCodeAt(i);
    const isSpace = c === 32 || c === 9 || c === 10 || c === 13 || c === 12 || c === 11 || c === 160;
    if (isSpace) {
      if (lastWasSpace) continue;
      norm += ' ';
      map.push(i);
      lastWasSpace = true;
    } else {
      norm += text[i].toLowerCase();
      map.push(i);
      lastWasSpace = false;
    }
  }
  return { norm, map };
}

const normalizeQuote = (q) => String(q || '').replace(/\s+/g, ' ').trim().toLowerCase();

function locateQuote(index, quote) {
  const needle = normalizeQuote(quote);
  if (!needle || !index || !index.norm) return null;
  let at = index.norm.indexOf(needle);
  if (at < 0 && needle.length > 40) {
    at = index.norm.indexOf(needle.slice(0, 40));
    if (at >= 0) {
      const endIdx = Math.min(at + 39, index.map.length - 1);
      return { start: index.map[at], end: index.map[endIdx] + 1, partial: true };
    }
  }
  if (at < 0) return null;
  const endIdx = Math.min(at + needle.length - 1, index.map.length - 1);
  return { start: index.map[at], end: index.map[endIdx] + 1, partial: false };
}

// Which segment (and therefore which speaker, at which timestamp) contains a
// character offset.
function segmentAt(segments, offset) {
  let hit = null;
  for (let i = 0; i < segments.length; i += 1) {
    const s = segments[i];
    if (s.offset > offset) return hit && offset <= hit.end ? hit : s;
    hit = s;
  }
  return hit;
}

// Keep the sentence carrying the evidence, not the paragraph around it. If the
// value itself appears in one sentence, that is the sentence worth storing.
function splitSentences(s) {
  const out = [];
  let buf = '';
  for (let i = 0; i < s.length; i += 1) {
    buf += s[i];
    if (/[.!?]/.test(s[i]) && (i + 1 >= s.length || /\s/.test(s[i + 1]))) {
      out.push(buf.trim());
      buf = '';
    }
  }
  if (clean(buf)) out.push(buf.trim());
  return out.filter(Boolean);
}

function tightenQuote(quote, value) {
  const q = clean(quote).replace(/\s+/g, ' ');
  if (!q) return '';
  if (q.length <= QUOTE_MAX) return q;
  const needle = clean(value).toLowerCase();
  const sentences = splitSentences(q);
  if (needle) {
    const carrying = sentences.find((s) => s.toLowerCase().includes(needle));
    if (carrying) return truncate(carrying, QUOTE_MAX);
  }
  const first = sentences[0];
  if (first && first.length <= QUOTE_MAX) return first;
  const cut = q.slice(0, QUOTE_MAX);
  const lastSpace = cut.lastIndexOf(' ');
  return `${lastSpace > 60 ? cut.slice(0, lastSpace) : cut}…`;
}

// ---------------------------------------------------------------------------
// Schema surface. Same exclusions as DocumentExtract: relations, lifecycle and
// tags are not things a sentence fills in, and renaming a fact sheet is not an
// extraction. The backend allow-lists further on its own side.
// ---------------------------------------------------------------------------
const EXTRACTABLE_FIELDS = allFieldsFor('Application').filter(
  (f) => f.type !== FIELD_TYPES.RELATION
    && f.type !== FIELD_TYPES.LIFECYCLE
    && f.type !== FIELD_TYPES.TAGS
    && f.key !== 'name'
    && f.key !== 'catalogId'
);

const SECTIONS = [...new Set(EXTRACTABLE_FIELDS.map((f) => f.section))];

const FIELD_BY_KEY = (() => {
  const m = {};
  EXTRACTABLE_FIELDS.forEach((f) => { if (!m[f.key]) m[f.key] = f; });
  return m;
})();

// Interviews are where hosting, integrations, owners, RTO/RPO and the war
// stories come from; those live in these sections.
const DEFAULT_SECTIONS = SECTIONS.filter(
  (s) => s === 'Information'
    || s === 'Classification & Ownership'
    || /Cloud Transformation/i.test(s)
);

// ---------------------------------------------------------------------------
// Which applications is this meeting about? Deterministic, local, no model:
// scan the transcript for the names of the workspace's own applications. Short
// or generic names are not searched for — a false hit here sends the wrong
// slice to the endpoint and pollutes the proposal list.
// ---------------------------------------------------------------------------
// Shorter than the equivalent guard in RelationshipWizard, deliberately. There
// a false hit creates a WRONG LINK in the model; here it only means a few extra
// lines are sent and a proposal appears for review. Keeping that screen's
// 5-character floor would exclude "Epic" from a healthcare estate, which would
// gut this feature on the single application every one of these meetings is
// about. The generic-word stoplist below still applies.
const MIN_NAME_LEN = 4;
const COMMON_NAMES = new Set([
  'access', 'admin', 'analytics', 'application', 'applications', 'archive', 'backup',
  'billing', 'cloud', 'data', 'database', 'email', 'engine', 'imaging', 'intranet',
  'inventory', 'legacy', 'mail', 'main', 'network', 'office', 'other', 'platform',
  'portal', 'print', 'printing', 'remote', 'report', 'reports', 'reporting', 'scanner',
  'security', 'server', 'servers', 'service', 'services', 'shared', 'storage', 'support',
  'system', 'systems', 'teams', 'test', 'users', 'vendor', 'web', 'windows', 'sql',
]);

function isSearchableName(name) {
  const n = clean(name).toLowerCase();
  if (n.length < MIN_NAME_LEN) return false;
  if (!/[a-z]/.test(n)) return false;
  if (COMMON_NAMES.has(n)) return false;
  return true;
}

function mentionIndexes(segments, name) {
  const re = new RegExp(`(?:^|[^A-Za-z0-9])${escapeRe(name)}(?:$|[^A-Za-z0-9])`, 'i');
  const hits = [];
  segments.forEach((s, i) => { if (re.test(s.text)) hits.push(i); });
  return hits;
}

// The slice sent for one application: the lines that name it, plus context
// either side. Lines are copied VERBATIM, so a quote the model returns from the
// slice is still a literal substring of the full transcript — which is what
// lets the speaker/timestamp lookup work, and what keeps the backend's own
// literal-quote check meaningful.
function buildSlice(segments, hitIdx) {
  if (!hitIdx.length) return { text: '', segmentCount: 0 };
  const keep = new Set();
  hitIdx.forEach((i) => {
    for (let k = i - CONTEXT_SEGMENTS; k <= i + CONTEXT_SEGMENTS; k += 1) {
      if (k >= 0 && k < segments.length) keep.add(k);
    }
  });
  const ordered = [...keep].sort((a, b) => a - b);
  let out = '';
  let prev = null;
  let used = 0;
  for (let i = 0; i < ordered.length; i += 1) {
    const idx = ordered[i];
    const raw = segments[idx].raw;
    if (out && prev !== null && idx !== prev + 1) out += GAP_MARKER;
    else if (out) out += '\n';
    if (out.length + raw.length > MAX_SLICE_CHARS) break;
    out += raw;
    prev = idx;
    used += 1;
  }
  return { text: out, segmentCount: used };
}

// ---------------------------------------------------------------------------
// localStorage — this browser only, never the fact sheet store.
// ---------------------------------------------------------------------------
function readTranscripts(tid) {
  try {
    const raw = localStorage.getItem(lsKey(tid));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

// Newest first. On quota, give up transcript TEXT oldest-first, then whole
// records — the accepted-fact log is what the Sources tab needs and is the last
// thing surrendered.
function writeTranscripts(tid, list) {
  let working = list.slice(0, MAX_TRANSCRIPTS);
  const evicted = [];
  for (let attempt = 0; attempt < 8; attempt += 1) {
    try {
      localStorage.setItem(lsKey(tid), JSON.stringify(working));
      return { ok: true, list: working, evicted };
    } catch (e) {
      const snapshot = working;
      const withText = [];
      for (let i = 0; i < snapshot.length; i += 1) { if (snapshot[i].text) withText.push(i); }
      if (withText.length > 1) {
        const oldest = withText[withText.length - 1];
        evicted.push(working[oldest].title || 'untitled meeting');
        working = working.map((t, i) => (i === oldest ? { ...t, text: '', textEvicted: true } : t));
      } else if (working.length > 1) {
        evicted.push(working[working.length - 1].title || 'untitled meeting');
        working = working.slice(0, working.length - 1);
      } else {
        return { ok: false, list: working, evicted };
      }
    }
  }
  return { ok: false, list: working, evicted };
}

const newId = () => `mtg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

// ---------------------------------------------------------------------------
// Reading whatever the user dropped on us.
// ---------------------------------------------------------------------------
function looksBinary(s) {
  if (!s) return false;
  const n = Math.min(s.length, 4000);
  let bad = 0;
  for (let i = 0; i < n; i += 1) {
    const c = s.charCodeAt(i);
    if (c === 0xFFFD || c < 9 || (c > 13 && c < 32)) bad += 1;
  }
  return bad / n > 0.02;
}

const PASTE_INSTEAD = 'Open the transcript, select all, and paste it into the box — '
  + 'parsing works exactly the same on pasted text.';

async function readTranscriptFile(file) {
  const ext = String(file.name.split('.').pop() || '').toLowerCase();
  if (['pdf', 'docx', 'doc'].includes(ext)) {
    return {
      text: '',
      error: `“${file.name}” is a ${ext.toUpperCase()} file. This screen reads plain text captions and `
        + `transcripts only — it cannot open ${ext.toUpperCase()} in the browser and will not guess at `
        + `the contents. ${PASTE_INSTEAD}`,
    };
  }
  const raw = await file.text();
  if (looksBinary(raw)) {
    return { text: '', error: `“${file.name}” does not decode as text. ${PASTE_INSTEAD}` };
  }
  if (!TEXT_EXTS.includes(ext)) {
    return { text: raw, note: `Read “${file.name}” as plain text — check the parse summary looks right.` };
  }
  return { text: raw };
}

// ---------------------------------------------------------------------------
// Small presentational pieces.
// ---------------------------------------------------------------------------
const CONF_STYLES = {
  high: { label: 'high', color: GREEN, bg: '#E8F5E9' },
  medium: { label: 'medium', color: AMBER, bg: '#FFF4E0' },
  low: { label: 'low', color: RED, bg: '#FDECEA' },
};

function normalizeConfidence(c) {
  const v = String(c || '').toLowerCase();
  if (v.startsWith('h')) return 'high';
  if (v.startsWith('m')) return 'medium';
  if (v.startsWith('l')) return 'low';
  return 'low'; // unknown confidence is the weakest, never the strongest
}

function ConfidenceChip({ level }) {
  const s = CONF_STYLES[level] || CONF_STYLES.low;
  return (
    <Chip
      size="small"
      label={s.label}
      sx={{ height: 20, fontWeight: 700, color: s.color, bgcolor: s.bg, border: `1px solid ${s.color}33` }}
    />
  );
}

const STATUS_META = {
  located: {
    label: 'quoted',
    color: GREEN,
    bg: '#E8F5E9',
    help: 'The quote was found in this transcript, so the speaker and timestamp below are read from the transcript itself.',
  },
  unlocated: {
    label: 'quote not found in this transcript',
    color: AMBER,
    bg: '#FFF4E0',
    help: 'A quote came back but it does not appear in the transcript as written — most often because it spans a gap between the parts that were sent. Treat it as unverified.',
  },
  missing: {
    label: 'UNVERIFIED — no quote',
    color: AMBER,
    bg: '#FFF4E0',
    help: 'No supporting quote was returned, so there is nothing in the transcript to check this value against.',
  },
};
const STATUS_RANK = { located: 0, unlocated: 1, missing: 2 };

function ParseSummary({ parsed }) {
  if (!parsed || !parsed.charCount) return null;
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
      <Chip size="small" label={`read as ${FORMAT_LABEL[parsed.format]}`} sx={{ fontWeight: 700, bgcolor: '#EEF4FB', color: BLUE }} />
      <Chip size="small" variant="outlined" label={`${parsed.speakers.length} speaker${parsed.speakers.length === 1 ? '' : 's'}`} />
      <Chip
        size="small"
        variant="outlined"
        label={parsed.durationSec != null ? `runs to ${fmtClock(parsed.durationSec)}` : 'no timestamps found'}
        sx={{ color: parsed.durationSec != null ? undefined : GREY }}
      />
      <Chip size="small" variant="outlined" label={`${parsed.lineCount.toLocaleString()} lines`} />
      <Chip size="small" variant="outlined" label={`${parsed.segments.length.toLocaleString()} spoken segments`} />
      <Chip size="small" variant="outlined" label={`${parsed.charCount.toLocaleString()} characters`} />
    </Stack>
  );
}

// ---------------------------------------------------------------------------

export default function MeetingIntelligence({ tenantId, onOpen }) {
  const [tab, setTab] = useState(0);

  // ---- what is kept in this browser ---------------------------------------
  const [transcripts, setTranscripts] = useState([]);
  const [storageNote, setStorageNote] = useState('');
  const [activeId, setActiveId] = useState('');

  // ---- tab 1 draft ---------------------------------------------------------
  const [draft, setDraft] = useState('');
  const [origin, setOrigin] = useState('');          // 'pasted' | 'file:<name>'
  const [fileErr, setFileErr] = useState('');
  const [fileNote, setFileNote] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [workspaceId, setWorkspaceId] = useState(tenantId || '');
  const [tenants, setTenants] = useState([]);
  const [savedNote, setSavedNote] = useState('');
  const fileRef = useRef(null);

  // ---- workspace data ------------------------------------------------------
  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  // ---- tab 2 run -----------------------------------------------------------
  const [selectedApps, setSelectedApps] = useState([]);
  const [sections, setSections] = useState(() => new Set(DEFAULT_SECTIONS));
  const [wholeTranscript, setWholeTranscript] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [runErr, setRunErr] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [dropped, setDropped] = useState(0);
  const [proposals, setProposals] = useState([]);
  const [decisions, setDecisions] = useState({});   // rowId -> {state, error, appName}
  const [savingId, setSavingId] = useState('');
  const [confirmRow, setConfirmRow] = useState(null);

  // ---- tab 3 ---------------------------------------------------------------
  const [detail, setDetail] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // ---- load ----------------------------------------------------------------
  useEffect(() => {
    setTranscripts(readTranscripts(tenantId));
    setActiveId('');
    setProposals([]);
    setDecisions({});
    // Inside a client workspace the answer is already known. From the master
    // workspace it is not, and guessing it would file a client's meeting
    // against the wrong estate — so it starts empty and must be chosen.
    setWorkspaceId(tenantId === 'optimum' ? '' : (tenantId || ''));
  }, [tenantId]);

  useEffect(() => {
    let alive = true;
    store.listTenants()
      .then((list) => { if (alive) setTenants((list || []).filter((t) => t.kind !== 'master')); })
      .catch(() => { if (alive) setTenants([]); });
    return () => { alive = false; };
  }, []);

  const reloadApps = useCallback(() => {
    setLoadingApps(true);
    setLoadErr('');
    store.listFactSheets(tenantId, { type: 'Application' })
      .then((list) => setApps(list || []))
      .catch(() => setLoadErr('Could not load applications for this workspace.'))
      .finally(() => setLoadingApps(false));
  }, [tenantId]);

  useEffect(() => { reloadApps(); }, [reloadApps]);

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getIdToken()}`,
        ...(options.headers || {}),
      },
    });
    const body = await res.text();
    let data = {};
    try { data = body ? JSON.parse(body) : {}; } catch (e) { data = {}; }
    if (!res.ok) {
      const err = new Error(data.error || data.message || `request failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return data;
  }, []);

  // ---- derived -------------------------------------------------------------
  const isMaster = tenantId === 'optimum';
  const draftParsed = useMemo(() => parseTranscript(draft), [draft]);

  const active = useMemo(
    () => transcripts.find((t) => t.id === activeId) || null,
    [transcripts, activeId]
  );
  const activeParsed = useMemo(
    () => parseTranscript(active ? active.text : ''),
    [active]
  );
  const activeIndex = useMemo(
    () => buildSearchIndex(activeParsed.text),
    [activeParsed]
  );

  // Applications the facts can be written to: the chosen workspace's own.
  const workspaceApps = useMemo(() => {
    const wid = (active && active.workspaceId) || workspaceId || tenantId;
    if (!wid || wid === 'optimum') return apps;
    return apps.filter((a) => a.tenantId === wid);
  }, [apps, active, workspaceId, tenantId]);

  // Deterministic, local: which of those applications are actually named in
  // this transcript, and where.
  const mentions = useMemo(() => {
    if (!activeParsed.segments.length) return [];
    return workspaceApps
      .filter((a) => isSearchableName(a.name))
      .map((a) => ({ app: a, hits: mentionIndexes(activeParsed.segments, a.name) }))
      .filter((m) => m.hits.length)
      .sort((a, b) => b.hits.length - a.hits.length);
  }, [workspaceApps, activeParsed]);

  // Opening a transcript pre-selects the applications it actually names, and
  // clears any review in progress.
  //
  // This keys off activeId, NOT off `mentions`. Accepting a fact updates the
  // local copy of that application, which recomputes `mentions` — and if the
  // reset ran on that, accepting one row would wipe every other proposal on
  // screen before the reviewer had judged them. The ref carries the current
  // mention list in without making it a trigger.
  const mentionsRef = useRef(mentions);
  mentionsRef.current = mentions;
  useEffect(() => {
    setSelectedApps(mentionsRef.current.slice(0, 12).map((m) => m.app));
    setProposals([]);
    setDecisions({});
    setRunErr('');
    setDropped(0);
  }, [activeId]);

  const askedFields = useMemo(
    () => [...new Set(EXTRACTABLE_FIELDS.filter((f) => sections.has(f.section)).map((f) => f.key))],
    [sections]
  );

  const fieldLabels = useMemo(() => {
    const m = {};
    askedFields.forEach((k) => { m[k] = FIELD_BY_KEY[k].label; });
    return m;
  }, [askedFields]);

  const persist = useCallback((list) => {
    const result = writeTranscripts(tenantId, list);
    setTranscripts(result.list);
    if (!result.ok) {
      setStorageNote('This browser refused to store the transcript — its local storage is full. '
        + 'The meeting record was kept but its text was not; delete an older transcript below and add it again.');
    } else if (result.evicted.length) {
      setStorageNote(`Local storage was full, so the text of ${result.evicted.length} older `
        + `transcript${result.evicted.length === 1 ? '' : 's'} was dropped to make room `
        + `(${result.evicted.slice(0, 3).join(', ')}). Their meeting records and accepted facts are intact.`);
    }
    return result.list;
  }, [tenantId]);

  const updateTranscript = useCallback((id, updater) => {
    setTranscripts((prev) => {
      const next = prev.map((t) => (t.id === id ? updater(t) : t));
      const result = writeTranscripts(tenantId, next);
      return result.list;
    });
  }, [tenantId]);

  // ---- tab 1 handlers ------------------------------------------------------
  const acceptFile = useCallback(async (file) => {
    if (!file) return;
    setFileErr(''); setFileNote(''); setSavedNote('');
    try {
      const result = await readTranscriptFile(file);
      if (result.error) { setFileErr(result.error); return; }
      setDraft(result.text);
      setOrigin(`file:${file.name}`);
      if (result.note) setFileNote(result.note);
      if (!clean(result.text)) setFileErr(`“${file.name}” is empty — there is nothing to parse.`);
      if (!clean(title)) setTitle(file.name.replace(/\.[^.]+$/, ''));
    } catch (e) {
      setFileErr(`Could not read “${file.name}”. ${PASTE_INSTEAD}`);
    }
  }, [title]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    acceptFile(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  const onPickFile = (e) => {
    acceptFile(e.target.files && e.target.files[0]);
    e.target.value = '';
  };

  const onDraftChange = (e) => {
    setDraft(e.target.value);
    setFileErr(''); setFileNote(''); setSavedNote('');
    if (!origin || origin === 'pasted') setOrigin(e.target.value.trim() ? 'pasted' : '');
  };

  const clearDraft = () => {
    setDraft(''); setOrigin(''); setFileErr(''); setFileNote(''); setSavedNote('');
  };

  const saveTranscript = () => {
    if (!clean(draft) || !clean(title)) return;
    const kept = draftParsed.text.slice(0, MAX_STORED_CHARS);
    const record = {
      id: newId(),
      title: clean(title),
      date: meetingDate,
      workspaceId: workspaceId || tenantId,
      addedAt: new Date().toISOString(),
      origin: origin || 'pasted',
      format: draftParsed.format,
      speakers: draftParsed.speakers,
      durationSec: draftParsed.durationSec,
      lineCount: draftParsed.lineCount,
      charCount: draftParsed.charCount,
      truncated: draftParsed.charCount > MAX_STORED_CHARS,
      text: kept,
      accepted: [],
    };
    persist([record, ...transcripts]);
    setActiveId(record.id);
    setSavedNote(`“${record.title}” is now held in this browser only. Open the Proposed facts tab to read it.`);
    setDraft(''); setOrigin(''); setTitle('');
    setTab(1);
  };

  const toggleSection = (s) => {
    const next = new Set(sections);
    if (next.has(s)) next.delete(s); else next.add(s);
    setSections(next);
  };

  // ---- the run -------------------------------------------------------------
  // One /ai/extract call per application, over the part of the transcript that
  // names it. Nothing is written by this step.
  const runExtract = async () => {
    if (!active || !selectedApps.length || !askedFields.length) return;
    setRunning(true);
    setRunErr('');
    setUnavailable(false);
    setDropped(0);
    setProgress(0);
    setProposals([]);
    setDecisions({});

    const collected = [];
    let droppedTotal = 0;
    const skipped = [];
    try {
      for (let i = 0; i < selectedApps.length; i += 1) {
        const app = selectedApps[i];
        const hits = mentionIndexes(activeParsed.segments, app.name);
        let slice = buildSlice(activeParsed.segments, hits);
        if (!slice.text) {
          if (!wholeTranscript) { skipped.push(app.name); setProgress(Math.round(((i + 1) / selectedApps.length) * 100)); continue; }
          slice = { text: activeParsed.text.slice(0, MAX_SLICE_CHARS), segmentCount: activeParsed.segments.length };
        }
        const payload = {
          text: slice.text,
          fields: askedFields,
          fieldLabels,
          // Context the endpoint may or may not use; ignoring it changes nothing.
          applicationName: app.name,
          factSheetId: app.id,
          source: `${active.title} (${active.date})`,
        };
        /* eslint-disable-next-line no-await-in-loop */
        const data = await api('/ai/extract', { method: 'POST', body: JSON.stringify(payload) });
        droppedTotal += Number(data.droppedUnverifiable || 0);
        // The endpoint returns { suggestions: { field: { value, evidence,
        // confidence } } }. Be as generous about the wrapper as DocumentExtract
        // is — but never invent a field the schema lacks, which is enforced
        // when the rows are built below.
        const suggestions = (data && (data.suggestions || data.fields || data.extracted)) || {};
        Object.keys(suggestions).forEach((key) => {
          const raw = suggestions[key];
          const s = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : { value: raw };
          collected.push({
            app,
            key,
            value: s.value,
            quote: s.evidence || s.quote || '',
            confidence: s.confidence,
          });
        });
        setProgress(Math.round(((i + 1) / selectedApps.length) * 100));
        /* eslint-disable-next-line no-await-in-loop */
        await new Promise((r) => setTimeout(r, 0));   // let the progress bar paint
      }

      // Best proposal per application+field: a located quote beats an unlocated
      // one, which beats none at all.
      const best = new Map();
      collected.forEach((item) => {
        const field = FIELD_BY_KEY[item.key];
        if (!field) return;                       // never invent a field the schema lacks
        const match = locateQuote(activeIndex, item.quote);
        const status = !clean(item.quote) ? 'missing' : (match ? 'located' : 'unlocated');
        const seg = match ? segmentAt(activeParsed.segments, match.start) : null;
        const coerced = coerceSuggestion(field, item.value);
        const current = (item.app.fields || {})[item.key];
        const rowId = `${item.app.id}::${item.key}`;
        const score = (status === 'located' ? 10 : 0) + (clean(item.quote) ? 3 : 0);
        const prior = best.get(rowId);
        if (prior && prior.score >= score) return;
        best.set(rowId, {
          rowId,
          score,
          appId: item.app.id,
          appName: item.app.name,
          fieldKey: item.key,
          fieldLabel: field.label,
          section: field.section,
          value: coerced.value,
          usable: coerced.ok,
          reason: coerced.reason,
          current,
          currentEmpty: isBlank(current),
          quote: clean(item.quote),
          storedQuote: tightenQuote(item.quote, coerced.value),
          status,
          partial: !!(match && match.partial),
          speaker: seg ? seg.speaker : '',
          time: seg ? seg.time : null,
          confidence: normalizeConfidence(item.confidence),
        });
      });

      const rows = [...best.values()].sort((a, b) =>
        STATUS_RANK[a.status] - STATUS_RANK[b.status]
        || a.appName.localeCompare(b.appName)
        || a.fieldLabel.localeCompare(b.fieldLabel));
      setProposals(rows);
      setDropped(droppedTotal);
      if (!rows.length) {
        setRunErr(skipped.length
          ? `Nothing was proposed. ${skipped.length} of the selected application${skipped.length === 1 ? ' is' : 's are'} never named in this transcript, so no text was sent for ${skipped.length === 1 ? 'it' : 'them'} (${skipped.slice(0, 4).join(', ')}).`
          : 'The service read the transcript and proposed nothing for these sections. That is a normal answer — an interview often contains no field-shaped facts.');
      } else if (skipped.length) {
        setRunErr(`${skipped.length} selected application${skipped.length === 1 ? '' : 's'} (${skipped.slice(0, 4).join(', ')}) ${skipped.length === 1 ? 'is' : 'are'} never named in this transcript, so nothing was sent for ${skipped.length === 1 ? 'it' : 'them'}.`);
      }
    } catch (e) {
      setUnavailable(true);
      setRunErr((e && e.message) || 'The extraction service could not be reached.');
    } finally {
      setRunning(false);
    }
  };

  // ---- accept / reject, one row at a time ----------------------------------
  const rejectRow = (row) => {
    setDecisions((prev) => ({ ...prev, [row.rowId]: { state: 'rejected' } }));
  };

  const acceptRow = async (row) => {
    if (!active) return;
    setConfirmRow(null);
    setSavingId(row.rowId);
    try {
      const fs = await store.getFactSheet(row.appId);
      if (!fs) throw new Error('that application could not be loaded');
      const existing = fs.fields || {};
      const stamp = new Date().toISOString();
      // The citation travels WITH the value, in the same write. The transcript
      // itself is not part of this object and never will be.
      const citations = { ...(existing.meetingCitations || {}) };
      citations[row.fieldKey] = {
        quote: row.storedQuote,
        speaker: row.speaker || '',
        timestamp: row.time != null ? fmtClock(row.time) : '',
        meetingTitle: active.title,
        meetingDate: active.date,
        verified: row.status === 'located',
        source: 'meeting transcript',
        at: stamp,
      };
      const priorKeys = Array.isArray(existing.meetingSourcedFields) ? existing.meetingSourcedFields : [];
      await store.saveFactSheet({
        ...fs,
        fields: {
          ...existing,
          [row.fieldKey]: row.value,
          meetingCitations: citations,
          meetingSourcedFields: [...new Set([...priorKeys, row.fieldKey])],
          lastMeetingSource: `${active.title} — ${active.date}`,
          lastMeetingSourceAt: stamp,
        },
      });

      // Keep the local app copy in step so a second proposal for the same field
      // shows the value that is now actually recorded.
      setApps((prev) => prev.map((a) => (a.id === row.appId
        ? { ...a, fields: { ...(a.fields || {}), [row.fieldKey]: row.value } }
        : a)));

      updateTranscript(active.id, (t) => ({
        ...t,
        accepted: [
          ...(t.accepted || []),
          {
            appId: row.appId,
            appName: row.appName,
            fieldKey: row.fieldKey,
            fieldLabel: row.fieldLabel,
            value: displayValue(row.value),
            quote: row.storedQuote,
            speaker: row.speaker || '',
            timestamp: row.time != null ? fmtClock(row.time) : '',
            verified: row.status === 'located',
            at: stamp,
          },
        ],
      }));
      setDecisions((prev) => ({ ...prev, [row.rowId]: { state: 'accepted' } }));
    } catch (e) {
      setDecisions((prev) => ({
        ...prev,
        [row.rowId]: { state: 'error', error: (e && e.message) || 'save failed' },
      }));
    } finally {
      setSavingId('');
    }
  };

  const deleteTranscript = (rec) => {
    setConfirmDelete(null);
    const next = transcripts.filter((t) => t.id !== rec.id);
    persist(next);
    if (activeId === rec.id) { setActiveId(''); setProposals([]); setDecisions({}); }
  };

  const openTranscript = (rec) => {
    setActiveId(rec.id);
    setTab(1);
  };

  // ---- render helpers ------------------------------------------------------
  const pending = proposals.filter((p) => !decisions[p.rowId] || decisions[p.rowId].state === 'error');
  const acceptedCount = proposals.filter((p) => decisions[p.rowId] && decisions[p.rowId].state === 'accepted').length;
  const canSave = !!clean(draft) && !!clean(title) && !!workspaceId;
  const workspaceName = (tenants.find((t) => t.id === workspaceId) || {}).name || workspaceId;

  const privacyNotice = (
    <Alert severity="info" icon={<LockOutlinedIcon fontSize="small" />} sx={{ mb: 2 }}>
      <AlertTitle sx={{ fontWeight: 700 }}>The transcript stays in this browser</AlertTitle>
      A recorded meeting is not a discovery document — the same hour carries contract, pricing and
      staffing talk alongside the architecture. So the transcript is held only in this browser
      (<code>{lsKey(tenantId)}</code>) and is <b>never</b> saved to the workspace. Only the facts you
      accept, and the short quote behind each one, are written to the fact sheet. To read it, slices of
      the transcript around each application&apos;s name are sent to the <code>/ai/extract</code>{' '}
      service — that is the one place any of this text leaves the browser.
    </Alert>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
        <RecordVoiceOverIcon sx={{ color: BLUE }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>Meeting intelligence</Typography>
        <Chip
          size="small"
          label="PROPOSALS ONLY — NOTHING IS SAVED UNTIL YOU ACCEPT IT"
          sx={{ height: 20, fontWeight: 700, bgcolor: '#EEF4FB', color: BLUE, border: `1px solid ${BLUE}44` }}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload applications">
          <span>
            <Button size="small" startIcon={<RefreshIcon />} onClick={reloadApps} disabled={running}>
              Reload
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
        Discovery knowledge is spoken in interviews and then lost. Every meeting platform already
        transcribes, so this reads the transcript you already have. It does not record, join or listen
        to anything.
      </Typography>

      {loadErr && <Alert severity="error" sx={{ mb: 2 }}>{loadErr}</Alert>}
      {storageNote && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setStorageNote('')}>{storageNote}</Alert>
      )}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: '1px solid #e5e9ef', mb: 2 }}>
        <Tab label="Add a transcript" sx={{ fontWeight: 700 }} />
        <Tab label={`Proposed facts${proposals.length ? ` (${pending.length})` : ''}`} sx={{ fontWeight: 700 }} />
        <Tab label={`Sources${transcripts.length ? ` (${transcripts.length})` : ''}`} sx={{ fontWeight: 700 }} />
      </Tabs>

      {/* ==================== TAB 1 — ADD A TRANSCRIPT ====================== */}
      {tab === 0 && (
        <>
          {privacyNotice}

          {savedNote && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSavedNote('')}>{savedNote}</Alert>}

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1.5 }}>
              1 · Paste or upload the transcript
            </Typography>

            {fileErr && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setFileErr('')}>{fileErr}</Alert>}
            {fileNote && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setFileNote('')}>{fileNote}</Alert>}

            <Box
              onClick={() => fileRef.current && fileRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              sx={{
                border: `2px dashed ${dragOver ? BLUE : '#cfd8dc'}`,
                borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer',
                bgcolor: dragOver ? '#f7fbff' : undefined,
                '&:hover': { borderColor: BLUE, bgcolor: '#f7fbff' },
              }}
            >
              <UploadFileIcon sx={{ fontSize: 40, color: BLUE }} />
              <Typography sx={{ fontWeight: 600, mt: 1 }}>
                Drop a transcript file here, or click to choose one
              </Typography>
              <Typography variant="body2" sx={{ color: GREY }}>
                .txt and .md (Grain, Teams and Zoom exports), .vtt and .srt captions. Timestamps and
                speaker labels are read out of all four.
              </Typography>
              <input ref={fileRef} type="file" accept={ACCEPT_ATTR} hidden onChange={onPickFile} />
            </Box>

            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" sx={{ color: GREY }}>or paste it</Typography>
            </Divider>

            <TextField
              multiline minRows={6} maxRows={16} fullWidth size="small"
              placeholder={'Paste the transcript here, e.g.\n\n00:04:12  Dana Whitfield\nWe run the interface engine on prem, Cloverleaf, and it feeds everything.\n\nor\n\nDana Whitfield: We run the interface engine on prem…'}
              value={draft} onChange={onDraftChange}
            />

            {!!clean(draft) && (
              <Box sx={{ mt: 1.5 }}>
                <ParseSummary parsed={draftParsed} />
                {!!draftParsed.speakers.length && (
                  <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: GREY }}>Speakers found:</Typography>
                    {draftParsed.speakers.slice(0, 12).map((s) => (
                      <Chip key={s} size="small" label={s} sx={{ height: 20, bgcolor: '#F4F7FA', color: NAVY }} />
                    ))}
                    {draftParsed.speakers.length > 12 && (
                      <Typography variant="caption" sx={{ color: GREY }}>
                        +{draftParsed.speakers.length - 12} more
                      </Typography>
                    )}
                  </Stack>
                )}
                {!draftParsed.speakers.length && (
                  <Typography variant="caption" sx={{ color: AMBER, display: 'block', mt: 1 }}>
                    No speaker labels were recognised. The transcript is still usable — quotes will be
                    saved without a speaker rather than with a guessed one.
                  </Typography>
                )}
                {draftParsed.charCount > MAX_STORED_CHARS && (
                  <Typography variant="caption" sx={{ color: AMBER, display: 'block', mt: 1 }}>
                    This transcript is {draftParsed.charCount.toLocaleString()} characters. Only the
                    first {MAX_STORED_CHARS.toLocaleString()} will be kept in this browser, so the
                    browser&apos;s storage cannot be filled by one long meeting — the rest is discarded,
                    not stored elsewhere.
                  </Typography>
                )}
                <Button size="small" onClick={clearDraft} sx={{ mt: 1 }}>Clear</Button>
              </Box>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1.5 }}>
              2 · Which meeting was this?
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
              <TextField
                size="small" label="Meeting title" sx={{ flex: 2 }}
                placeholder="e.g. Cardiology application owner interview"
                value={title} onChange={(e) => setTitle(e.target.value)}
              />
              <TextField
                size="small" label="Meeting date" type="date" sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
                value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)}
              />
              <TextField
                select size="small" label="Client workspace" sx={{ flex: 1.5 }}
                value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)}
                disabled={!isMaster}
                helperText={isMaster
                  ? 'Accepted facts are written to this client’s fact sheets'
                  : 'You are working inside this workspace'}
              >
                {(isMaster ? tenants : tenants.filter((t) => t.id === tenantId)).map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
                {!!workspaceId && !tenants.some((t) => t.id === workspaceId) && (
                  <MenuItem value={workspaceId}>{workspaceId}</MenuItem>
                )}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Button
                variant="contained" disabled={!canSave} onClick={saveTranscript}
                sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
              >
                Keep this transcript in my browser
              </Button>
              <Typography variant="caption" sx={{ color: GREY }}>
                {canSave
                  ? `Held under ${lsKey(tenantId)} · filed to ${workspaceName} · nothing is sent anywhere by this step`
                  : `Needed first: ${[!clean(title) && 'a meeting title', !clean(draft) && 'a transcript', !workspaceId && 'a client workspace'].filter(Boolean).join(', ')}`}
              </Typography>
            </Stack>
          </Paper>

          {/* ---- honest dead ends ---------------------------------------- */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1 }}>
              Pulling transcripts directly from Grain or Teams
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
              <Tooltip title="Requires a Grain API credential. None is configured in this environment.">
                <span>
                  <Button variant="outlined" disabled startIcon={<CloudOffIcon />}>
                    Pull from Grain
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Requires a Microsoft Graph application credential with transcript scope. None is configured in this environment.">
                <span>
                  <Button variant="outlined" disabled startIcon={<CloudOffIcon />}>
                    Pull from Microsoft Teams
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            <Typography variant="body2" sx={{ color: GREY }}>
              These two are disabled because they need an API credential that is <b>not configured</b> —
              a Grain API key, or a Microsoft Graph application credential with permission to read
              meeting transcripts. Neither exists in this environment, so the buttons do nothing rather
              than fail in a way that looks like a bug. Until one is configured, every transcript here
              arrives by paste or upload, and the Sources tab records which of the two it was.
            </Typography>
          </Paper>
        </>
      )}

      {/* ==================== TAB 2 — PROPOSED FACTS ======================== */}
      {tab === 1 && (
        <>
          {!active ? (
            <Alert severity="info">
              <AlertTitle>No transcript is open</AlertTitle>
              Add one on the first tab, or open one from Sources. Nothing on this screen reads a
              meeting you have not handed it.
            </Alert>
          ) : (
            <>
              {privacyNotice}

              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                    1 · {active.title}
                  </Typography>
                  <Chip size="small" variant="outlined" label={active.date} />
                  <Chip
                    size="small" variant="outlined"
                    label={active.origin && active.origin.startsWith('file:')
                      ? `uploaded ${active.origin.slice(5)}`
                      : 'pasted by hand'}
                    sx={{ color: GREY }}
                  />
                </Stack>
                <ParseSummary parsed={activeParsed} />
                {active.truncated && (
                  <Typography variant="caption" sx={{ color: AMBER, display: 'block', mt: 1 }}>
                    Only the first {MAX_STORED_CHARS.toLocaleString()} characters of this meeting were
                    kept, so anything said after that point cannot be read or quoted here.
                  </Typography>
                )}
                {active.textEvicted && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    The text of this transcript was dropped to free browser storage. Its record and its
                    accepted facts survive, but it cannot be read again unless you add it back.
                  </Alert>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1 }}>
                  2 · Which applications did this meeting cover?
                </Typography>
                <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1.5 }}>
                  {mentions.length
                    ? `${mentions.length} of this workspace’s applications are named somewhere in the transcript and are pre-selected. Only the lines naming an application — plus ${CONTEXT_SEGMENTS} lines either side — are sent for it, so the commercial and personal parts of the meeting are not.`
                    : 'No application in this workspace is named in the transcript by its recorded name. Pick the ones the meeting was about, or add the names to the fact sheets first.'}
                </Typography>

                <Autocomplete
                  multiple size="small" sx={{ mb: 2 }}
                  options={workspaceApps}
                  loading={loadingApps}
                  disabled={running}
                  value={selectedApps}
                  onChange={(e, v) => setSelectedApps(v)}
                  getOptionLabel={(o) => (o && o.name) || ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderOption={(props, o) => {
                    const m = mentions.find((x) => x.app.id === o.id);
                    return (
                      <li {...props} key={o.id}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.name}</Typography>
                          <Typography variant="caption" sx={{ color: m ? GREEN : GREY }}>
                            {m ? `named on ${m.hits.length} line${m.hits.length === 1 ? '' : 's'}` : 'not named in this transcript'}
                          </Typography>
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Applications to read for"
                      placeholder="Search applications…"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingApps ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Which sections should be looked for?
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 1 }}>
                  {SECTIONS.map((s) => (
                    <FormControlLabel
                      key={s}
                      sx={{ m: 0, mr: 1.5 }}
                      control={(
                        <Checkbox
                          size="small" checked={sections.has(s)}
                          onChange={() => toggleSection(s)} disabled={running}
                        />
                      )}
                      label={<span style={{ fontSize: 13 }}>{s}</span>}
                    />
                  ))}
                </Box>

                <FormControlLabel
                  sx={{ m: 0, display: 'block' }}
                  control={(
                    <Checkbox
                      size="small" checked={wholeTranscript} disabled={running}
                      onChange={(e) => setWholeTranscript(e.target.checked)}
                    />
                  )}
                  label={(
                    <span style={{ fontSize: 13 }}>
                      For an application that is never named, send the whole transcript instead of
                      skipping it — use only when the meeting was about one system that people
                      referred to obliquely (&ldquo;the EMR&rdquo;), and note it sends the commercial
                      parts of the meeting too.
                    </span>
                  )}
                />

                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
                  <Button
                    variant="contained" startIcon={<FormatQuoteIcon />}
                    disabled={running || !selectedApps.length || !askedFields.length || !clean(active.text)}
                    onClick={runExtract}
                    sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
                  >
                    {running ? `Reading ${progress}%` : 'Read the transcript for these applications'}
                  </Button>
                  <Typography variant="caption" sx={{ color: GREY }}>
                    {selectedApps.length} application{selectedApps.length === 1 ? '' : 's'} ·{' '}
                    {askedFields.length} field{askedFields.length === 1 ? '' : 's'} in scope · this step
                    writes nothing
                  </Typography>
                </Stack>
                {running && <LinearProgress variant="determinate" value={progress} sx={{ mt: 1.5 }} />}
              </Paper>

              {unavailable && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>Transcript reading is not enabled for this environment</AlertTitle>
                  The <code>/ai/extract</code> service did not respond{runErr ? ` (${runErr})` : ''}.
                  Nothing was changed and nothing was lost — the transcript is still in this browser.
                </Alert>
              )}
              {!unavailable && runErr && (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRunErr('')}>{runErr}</Alert>
              )}
              {dropped > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  The service discarded <b>{dropped}</b> proposal{dropped === 1 ? '' : 's'} whose quoted
                  evidence did not occur literally in the text it was given. Those never reached this
                  screen, which is the intended behaviour — an unquotable claim about a client&apos;s
                  estate is not a finding.
                </Alert>
              )}

              {!!proposals.length && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                      3 · Accept or reject each proposal
                    </Typography>
                    <Chip size="small" label={`${proposals.length} proposed`} sx={{ fontWeight: 600 }} />
                    <Chip
                      size="small" label={`${acceptedCount} accepted`}
                      sx={{ fontWeight: 600, bgcolor: acceptedCount ? '#E8F5E9' : undefined, color: acceptedCount ? GREEN : undefined }}
                    />
                  </Stack>

                  <Alert severity="warning" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 1.5 }}>
                    These are <b>proposals, not findings</b>. Accepting one writes the value to the fact
                    sheet together with its quote, the speaker and the meeting — and{' '}
                    <b>that quote is then visible to anyone who can see this workspace</b>. Read the
                    quote before you accept it: it is a fragment of a real conversation.
                  </Alert>

                  <Paper variant="outlined" sx={{ maxHeight: 620, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={headSx}>
                          <TableCell>Application</TableCell>
                          <TableCell>Field</TableCell>
                          <TableCell>Currently recorded</TableCell>
                          <TableCell>Proposed value</TableCell>
                          <TableCell>Evidence from the transcript</TableCell>
                          <TableCell>Confidence</TableCell>
                          <TableCell align="center">Decision</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {proposals.map((r) => {
                          const meta = STATUS_META[r.status];
                          const decision = decisions[r.rowId] || {};
                          const done = decision.state === 'accepted' || decision.state === 'rejected';
                          return (
                            <TableRow
                              key={r.rowId}
                              hover
                              sx={{
                                opacity: decision.state === 'rejected' ? 0.55 : 1,
                                ...(r.status !== 'located'
                                  ? { bgcolor: '#FFFBF2', '& td:first-of-type': { borderLeft: `3px solid ${AMBER}` } }
                                  : {}),
                              }}
                            >
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Box
                                  component="span"
                                  onClick={() => onOpen && onOpen(r.appId)}
                                  sx={{
                                    fontWeight: 700, color: NAVY,
                                    cursor: onOpen ? 'pointer' : 'default',
                                    '&:hover': { textDecoration: onOpen ? 'underline' : 'none' },
                                  }}
                                >
                                  {r.appName}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ color: GREY, whiteSpace: 'nowrap' }}>
                                {r.fieldLabel}
                                <Typography variant="caption" sx={{ color: GREY, display: 'block' }}>
                                  {r.section}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ maxWidth: 160, color: r.currentEmpty ? '#C77700' : NAVY }}>
                                {r.currentEmpty ? <em>empty</em> : (
                                  <Tooltip title={displayValue(r.current)}>
                                    <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {displayValue(r.current)}
                                    </Box>
                                  </Tooltip>
                                )}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 220, fontWeight: 600 }}>
                                {displayValue(r.value)}
                                {!r.usable && (
                                  <Chip
                                    size="small" label={`cannot be accepted — ${r.reason}`}
                                    sx={{ ml: 0.5, height: 18, fontWeight: 700, bgcolor: '#FDECEA', color: RED }}
                                  />
                                )}
                                {r.usable && !r.currentEmpty && (
                                  <Chip
                                    size="small" label="would replace"
                                    sx={{ ml: 0.5, height: 18, fontWeight: 700, bgcolor: '#FFF4E0', color: AMBER }}
                                  />
                                )}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 380 }}>
                                {r.status === 'located' ? (
                                  <Box>
                                    <Box
                                      component="span"
                                      sx={{
                                        display: 'block', fontStyle: 'italic', color: NAVY,
                                        borderLeft: `3px solid ${GREEN}`, pl: 1, py: 0.25,
                                      }}
                                    >
                                      “{r.storedQuote}”
                                    </Box>
                                    <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 0.5 }}>
                                      {r.speaker ? <b>{r.speaker}</b> : <i>speaker not labelled</i>}
                                      {r.time != null ? ` · ${fmtClock(r.time)}` : ' · no timestamp'}
                                    </Typography>
                                    {r.quote.length > r.storedQuote.length && (
                                      <Typography variant="caption" sx={{ color: GREY, display: 'block' }}>
                                        shortened to the sentence carrying the evidence — that is what gets stored
                                      </Typography>
                                    )}
                                    {r.partial && (
                                      <Typography variant="caption" sx={{ color: AMBER, display: 'block' }}>
                                        only the opening of this quote matched the transcript
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Box>
                                    <Tooltip title={meta.help}>
                                      <Chip
                                        size="small" label={meta.label}
                                        sx={{ height: 18, fontWeight: 700, color: meta.color, bgcolor: meta.bg, border: `1px solid ${meta.color}` }}
                                      />
                                    </Tooltip>
                                    {r.status === 'unlocated' && (
                                      <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                        “{truncate(r.quote, 140)}”
                                      </Typography>
                                    )}
                                    <Typography variant="caption" sx={{ color: AMBER, display: 'block', mt: 0.5 }}>
                                      {r.status === 'missing'
                                        ? 'Nothing in the meeting was cited for this, so there is no way to confirm who said it or when.'
                                        : 'The cited words are not in this transcript as written, so no speaker or timestamp can be attached.'}
                                    </Typography>
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell><ConfidenceChip level={r.confidence} /></TableCell>
                              <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                {decision.state === 'accepted' && (
                                  <Chip size="small" label="saved to the fact sheet" sx={{ height: 20, fontWeight: 700, bgcolor: '#E8F5E9', color: GREEN }} />
                                )}
                                {decision.state === 'rejected' && (
                                  <Chip size="small" label="rejected" sx={{ height: 20, fontWeight: 700, bgcolor: '#F4F7FA', color: GREY }} />
                                )}
                                {!done && (
                                  <Stack direction="row" spacing={0.5} justifyContent="center">
                                    <Tooltip title={r.usable
                                      ? 'Write this value and its citation to the fact sheet'
                                      : `Cannot be accepted: the proposed value is ${r.reason}`}>
                                      <span>
                                        <Button
                                          size="small" variant="contained"
                                          disabled={!r.usable || savingId === r.rowId}
                                          onClick={() => (r.status === 'located' && r.currentEmpty
                                            ? acceptRow(r)
                                            : setConfirmRow(r))}
                                          sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
                                        >
                                          {savingId === r.rowId ? 'Saving…' : 'Accept'}
                                        </Button>
                                      </span>
                                    </Tooltip>
                                    <Button size="small" onClick={() => rejectRow(r)} disabled={savingId === r.rowId}>
                                      Reject
                                    </Button>
                                  </Stack>
                                )}
                                {decision.state === 'error' && (
                                  <Typography variant="caption" sx={{ color: RED, display: 'block' }}>
                                    not saved — {decision.error}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Paper>

                  <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1.5 }}>
                    Proposals whose quote could not be found in the transcript, and those returned with
                    no quote at all, are marked and sorted last. They can still be accepted — a person
                    who was in the room may know they are right — but they are saved marked unverified.
                  </Typography>
                </Paper>
              )}
            </>
          )}
        </>
      )}

      {/* ==================== TAB 3 — SOURCES =============================== */}
      {tab === 2 && (
        <>
          <Alert severity="info" icon={<LockOutlinedIcon fontSize="small" />} sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>Provenance</AlertTitle>
            Every transcript this browser holds for this workspace, and how many facts came out of each.
            Six months from now, &ldquo;where did we learn that?&rdquo; is answerable from here and from
            the citation stored on the fact sheet itself. The transcripts live only in this browser, so
            a colleague on another machine sees the accepted facts and their quotes, not the meeting.
          </Alert>

          {!transcripts.length ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: GREY }}>
                No transcripts have been added in this browser for this workspace.
              </Typography>
            </Paper>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={headSx}>
                    <TableCell>Meeting</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>How it arrived</TableCell>
                    <TableCell>Speakers</TableCell>
                    <TableCell>Length</TableCell>
                    <TableCell align="right">Facts accepted</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transcripts.map((t) => {
                    const count = (t.accepted || []).length;
                    return (
                      <TableRow key={t.id} hover selected={t.id === activeId}>
                        <TableCell sx={{ fontWeight: 700, color: NAVY }}>
                          {t.title}
                          <Typography variant="caption" sx={{ color: GREY, display: 'block' }}>
                            filed to {(tenants.find((x) => x.id === t.workspaceId) || {}).name || t.workspaceId}
                            {t.truncated ? ' · stored truncated' : ''}
                            {t.textEvicted ? ' · text dropped for storage space' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.date}</TableCell>
                        <TableCell sx={{ color: GREY, whiteSpace: 'nowrap' }}>
                          {t.origin && t.origin.startsWith('file:')
                            ? `uploaded — ${t.origin.slice(5)}`
                            : 'pasted by hand'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Tooltip title={(t.speakers || []).join(', ') || 'none recognised'}>
                            <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: GREY }}>
                              {(t.speakers || []).length
                                ? `${t.speakers.length} · ${t.speakers.slice(0, 3).join(', ')}`
                                : 'none recognised'}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ color: GREY, whiteSpace: 'nowrap' }}>
                          {t.durationSec != null ? fmtClock(t.durationSec) : '—'}
                          {` · ${(t.lineCount || 0).toLocaleString()} lines`}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={count}
                            onClick={count ? () => setDetail(t) : undefined}
                            sx={{
                              fontWeight: 700,
                              bgcolor: count ? '#E8F5E9' : '#F4F7FA',
                              color: count ? GREEN : GREY,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title={t.text ? 'Open this transcript for extraction' : 'The text of this transcript is no longer in this browser'}>
                            <span>
                              <Button size="small" onClick={() => openTranscript(t)} disabled={!t.text}>
                                Open
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Remove from this browser">
                            <span>
                              <Button
                                size="small" startIcon={<DeleteOutlineIcon />} sx={{ color: RED }}
                                onClick={() => setConfirmDelete(t)}
                              >
                                Delete
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      {/* ---- confirm an accept that replaces or is unverified -------------- */}
      <Dialog open={!!confirmRow} onClose={() => setConfirmRow(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Accept this proposal?</DialogTitle>
        <DialogContent dividers>
          {confirmRow && (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <b>{confirmRow.fieldLabel}</b> on <b>{confirmRow.appName}</b> will be set to{' '}
                <b>{displayValue(confirmRow.value)}</b>.
              </Typography>
              {!confirmRow.currentEmpty && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  This replaces the recorded value <b>{displayValue(confirmRow.current)}</b>.
                </Alert>
              )}
              {confirmRow.status !== 'located' && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  {confirmRow.status === 'missing'
                    ? 'No quote was returned for this value, so it will be saved marked unverified with no speaker and no timestamp.'
                    : 'The quote could not be found in this transcript, so it will be saved marked unverified.'}
                </Alert>
              )}
              {confirmRow.status === 'located' && (
                <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#FAFBFC', border: '1px solid #E3E8EE', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: NAVY }}>
                    “{confirmRow.storedQuote}”
                  </Typography>
                  <Typography variant="caption" sx={{ color: GREY }}>
                    {confirmRow.speaker || 'speaker not labelled'}
                    {confirmRow.time != null ? ` · ${fmtClock(confirmRow.time)}` : ''}
                    {active ? ` · ${active.title}, ${active.date}` : ''}
                  </Typography>
                </Box>
              )}
              <Typography variant="caption" sx={{ color: GREY }}>
                The quote above is stored with the value and is visible to anyone who can see this
                workspace. The transcript itself is not saved anywhere but this browser.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRow(null)}>Cancel</Button>
          <Button
            variant="contained" onClick={() => acceptRow(confirmRow)}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
          >
            Accept and save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- what came out of one meeting --------------------------------- */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>
          Facts accepted from {detail ? detail.title : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow sx={headSx}>
                <TableCell>Application</TableCell>
                <TableCell>Field</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Quote saved with it</TableCell>
                <TableCell align="right">Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {((detail && detail.accepted) || []).map((a, i) => (
                <TableRow key={`${a.appId}-${a.fieldKey}-${i}`}>
                  <TableCell sx={{ fontWeight: 600, color: NAVY }}>{a.appName}</TableCell>
                  <TableCell sx={{ color: GREY }}>{a.fieldLabel}</TableCell>
                  <TableCell>{a.value}</TableCell>
                  <TableCell sx={{ maxWidth: 380 }}>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>“{a.quote}”</Typography>
                    <Typography variant="caption" sx={{ color: GREY }}>
                      {a.speaker || 'speaker not labelled'}
                      {a.timestamp ? ` · ${a.timestamp}` : ''}
                      {a.verified ? '' : ' · unverified'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open the fact sheet">
                      <span>
                        <Button
                          size="small" startIcon={<OpenInNewIcon />}
                          disabled={!onOpen} onClick={() => onOpen && onOpen(a.appId)}
                        >
                          Open
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ---- delete a transcript from this browser ------------------------ */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Delete this transcript?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            “{confirmDelete ? confirmDelete.title : ''}” is removed from this browser, along with the
            record of which facts came from it. The facts already accepted stay on their fact sheets
            with their quotes — but this source row, which is how anyone traces them back, is gone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            variant="contained" sx={{ bgcolor: RED, '&:hover': { bgcolor: '#8E2B24' } }}
            onClick={() => deleteTranscript(confirmDelete)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
